// Wrapper tenant per ServiceModelPicker (ora in @easyflow/ui-ai-routing).
// Inietta apiClient + notify così i callsite restano invariati.
import {
  ServiceModelPicker,
  type ServiceModelPickerProps,
  type ApiClientLike,
  type ApiClientRequestOptions,
} from '@easyflow/ui-ai-routing';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ServiceRoutingRevisionSchema } from '@easyflow/shared';

type WrapperProps = Omit<ServiceModelPickerProps, 'apiClient' | 'notify'>;

const baseClient = apiClient as unknown as ApiClientLike;

// La lista dei modelli del tenant è la stessa per tutti i picker della
// pagina: le istanze montate insieme condividono una sola richiesta.
// Qualunque scrittura svuota la cache, così il valore salvato si rilegge.
const SHARED_GET_URLS: ReadonlySet<string> = new Set(['/ai-tools/service-models/']);
const SHARED_GET_TTL_MS = 2000;
const sharedGets = new Map<string, { at: number; promise: Promise<{ data: unknown }> }>();
const observedRevisions = new Map<string, number>();

/** Da chiamare dopo una scrittura fatta fuori dai picker (es. cambio provider):
 *  il modello salvato dal backend va riletto, non preso dalla cache. */
export function clearSharedPickerReads(): void {
  sharedGets.clear();
  observedRevisions.clear();
}

function scopedPickerClient(scope: string): ApiClientLike {
 return {
  get<T = unknown>(url: string, opts?: ApiClientRequestOptions): Promise<{ data: T }> {
    if (!SHARED_GET_URLS.has(url)) return baseClient.get<T>(url, opts);
    const key = `${scope}:${url}`;
    const hit = sharedGets.get(key);
    if (hit && Date.now() - hit.at < SHARED_GET_TTL_MS) return hit.promise as Promise<{ data: T }>;
    // Richiesta condivisa senza signal: ogni picker scarta da sé il risultato
    // se nel frattempo è stato smontato (controlla `signal.aborted`).
    const promise = baseClient.get<unknown>(url).then(response => {
      const revisions = ServiceRoutingRevisionSchema.array().safeParse(response.data);
      if (revisions.success) {
        for (const row of revisions.data) observedRevisions.set(`${scope}:${row.service_key}`, row.governance_revision);
      }
      return response;
    });
    sharedGets.set(key, { at: Date.now(), promise });
    promise.catch(() => { sharedGets.delete(key); });
    return promise as Promise<{ data: T }>;
  },
  put<T = unknown>(url: string, body?: unknown, opts?: ApiClientRequestOptions): Promise<{ data: T }> {
    const serviceKey = url.startsWith('/ai-tools/service-models/') ? url.slice('/ai-tools/service-models/'.length) : null;
    const revision = serviceKey ? observedRevisions.get(`${scope}:${serviceKey}`) : undefined;
    const governedBody = revision !== undefined && body !== null && typeof body === 'object' && !Array.isArray(body)
      ? { ...body, expected_revision: revision } : body;
    sharedGets.clear();
    return baseClient.put<T>(url, governedBody, opts);
  },
  post<T = unknown>(url: string, body?: unknown, opts?: ApiClientRequestOptions): Promise<{ data: T }> {
    sharedGets.clear();
    return baseClient.post<T>(url, body, opts);
  },
 };
}

export const tenantPickerClient: ApiClientLike = scopedPickerClient('legacy');

export default function TenantServiceModelPicker(props: WrapperProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const scope = `${user?.tenantId ?? user?.tenant_id ?? 'none'}:${user?.id ?? 'anonymous'}`;
  const client = useMemo(() => scopedPickerClient(scope), [scope]);
  return <ServiceModelPicker {...props} apiClient={client} notify={toast} />;
}
